import { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchAppById } from '../store/appsSlice'
import { fetchActivity, fetchActivityIncremental, clearActivity, setPolling } from '../store/activitySlice'
import { fetchBugReports, clearBugReports } from '../store/bugReportsSlice'
import { fetchChats, clearChats } from '../store/chatSlice'
import { clearDocs } from '../store/docsSlice'
import AppHeader from '../components/AppHeader'
import AppActions from '../components/AppActions'
import BugReportList from '../components/BugReportList'
import ChatList from '../components/ChatList'
import TaskTimeline from '../components/TaskTimeline'
import DocsSection from '../components/DocsSection'
import ContainerList from '../components/ContainerList'
import AppEditors from '../components/AppEditors'
import './AppPage.css'

const POLL_INTERVAL = 10000

function AppPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const app = useAppSelector((state) => state.apps.items.find((a) => a.id === id))
  const detailError = useAppSelector((state) => state.apps.detailError)
  const activityTasks = useAppSelector((state) => state.activity.tasks)
  const activityContainers = useAppSelector((state) => state.activity.containers)
  const activityLoading = useAppSelector((state) => state.activity.loading)
  const activityError = useAppSelector((state) => state.activity.error)
  const lastFetchedAt = useAppSelector((state) => state.activity.lastFetchedAt)
  const chats = useAppSelector((state) => state.chats.items)
  const hasActiveChat = useMemo(() => chats.some((c) => c.status === 'active'), [chats])
  const hasRunningTasks = useMemo(() => activityTasks.some((t) => t.status === 'in_progress'), [activityTasks])
  const lastFetchedAtRef = useRef(lastFetchedAt)
  useEffect(() => { lastFetchedAtRef.current = lastFetchedAt }, [lastFetchedAt])

  const BUG_SUBMIT_POLL_DURATION = 30000
  const lastBugSubmittedAt = useAppSelector((state) => state.bugReports.lastSubmittedAt)
  const [hasRecentBugSubmission, setHasRecentBugSubmission] = useState(false)

  useEffect(() => {
    if (!lastBugSubmittedAt) {
      setHasRecentBugSubmission(false)
      return
    }
    const elapsed = Date.now() - lastBugSubmittedAt
    if (elapsed < BUG_SUBMIT_POLL_DURATION) {
      setHasRecentBugSubmission(true)
      const timer = setTimeout(() => {
        setHasRecentBugSubmission(false)
      }, BUG_SUBMIT_POLL_DURATION - elapsed)
      return () => clearTimeout(timer)
    } else {
      setHasRecentBugSubmission(false)
    }
  }, [lastBugSubmittedAt])

  useEffect(() => {
    if (app?.name) {
      document.title = `Loop Builder: ${app.name}`
    }
    return () => {
      document.title = 'Loop Builder'
    }
  }, [app?.name])

  useEffect(() => {
    if (id) {
      dispatch(fetchAppById(id))
      dispatch(fetchActivity(id))
      dispatch(fetchBugReports(id))
      dispatch(fetchChats(id))
    }
    return () => {
      dispatch(clearActivity())
      dispatch(clearBugReports())
      dispatch(clearChats())
      dispatch(clearDocs())
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!id || !app) return
    const shouldPoll = app.status === 'building' || hasActiveChat || hasRunningTasks || hasRecentBugSubmission
    dispatch(setPolling(shouldPoll))
    if (!shouldPoll) return

    const poll = () => {
      if (document.visibilityState !== 'visible') return
      dispatch(fetchAppById(id))
      if (lastFetchedAtRef.current) {
        dispatch(fetchActivityIncremental({ appId: id, since: lastFetchedAtRef.current }))
      } else {
        dispatch(fetchActivity(id))
      }
      dispatch(fetchBugReports(id))
      dispatch(fetchChats(id))
    }

    const interval = setInterval(poll, POLL_INTERVAL)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      dispatch(setPolling(false))
    }
  }, [dispatch, id, app?.status, hasActiveChat, hasRunningTasks, hasRecentBugSubmission])

  const handleRefresh = useCallback(() => {
    if (id) {
      dispatch(fetchAppById(id))
      if (lastFetchedAtRef.current) {
        dispatch(fetchActivityIncremental({ appId: id, since: lastFetchedAtRef.current }))
      } else {
        dispatch(fetchActivity(id))
      }
      dispatch(fetchBugReports(id))
      dispatch(fetchChats(id))
    }
  }, [dispatch, id])

  if (!app && detailError) {
    return (
      <main className="app-page" data-testid="app-page">
        <nav aria-label="Breadcrumb">
          <button
            className="app-page__back"
            onClick={() => navigate('/')}
            data-testid="app-page-back"
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Back to Dashboard
          </button>
        </nav>
        <div className="app-page__error" data-testid="app-page-error" role="alert">
          <h1 className="app-page__error-title">App unavailable</h1>
          <p className="app-page__error-message" data-testid="app-page-error-message">{detailError}</p>
          <button
            className="app-page__error-action"
            onClick={() => navigate('/')}
            data-testid="app-page-error-back"
          >
            Back to Dashboard
          </button>
        </div>
      </main>
    )
  }

  if (!app) {
    return (
      <main className="app-page" data-testid="app-page" aria-busy="true">
        <div className="app-page__loading" data-testid="app-page-loading" role="status">
          <span className="sr-only">Loading app details...</span>
          <div className="app-page__loading-header">
            <div className="skeleton-line skeleton-line--title" style={{ width: '40%' }} />
            <div className="skeleton-line skeleton-line--text" style={{ width: '70%' }} />
            <div className="skeleton-line skeleton-line--meta" style={{ width: '30%' }} />
          </div>
          <div className="app-page__loading-actions">
            <div className="skeleton-line" style={{ width: 140, height: 40, borderRadius: 'var(--radius-sm)' }} />
            <div className="skeleton-line" style={{ width: 160, height: 40, borderRadius: 'var(--radius-sm)' }} />
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="app-page" data-testid="app-page">
      <div className="app-page__sticky-header">
        <nav aria-label="Breadcrumb">
          <button
            className="app-page__back"
            onClick={() => navigate('/')}
            data-testid="app-page-back"
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Back to Dashboard
          </button>
        </nav>
        <AppHeader app={app} />
        <AppActions app={app} />
      </div>
      <BugReportList appId={app.id} />
      <ChatList appId={app.id} prEnabled={!!app.create_pull_requests} />
      <TaskTimeline
        tasks={activityTasks}
        loading={activityLoading}
        error={activityError}
        appId={app.id}
        appStatus={app.status}
        onRefresh={handleRefresh}
      />
      <DocsSection appId={app.id} />
      <ContainerList
        containers={activityContainers}
        appId={app.id}
        onRefresh={handleRefresh}
      />
      <AppEditors appId={app.id} />
    </main>
  )
}

export default AppPage
